import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { HERO_IMAGE } from "../data/content";
import { useScrollToSection } from "../utils/scroll";

const ease = [0.22, 1, 0.36, 1];
const DELAY = 1.35; // wait for the intro curtain

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay, ease }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const scrollTo = useScrollToSection();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      id="home"
      ref={ref}
      className="relative flex min-h-[100svh] items-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* parallax background */}
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <img
          src={HERO_IMAGE}
          alt="Hope Giver volunteers handing water and essential supplies to community members"
          className="h-full w-full object-cover"
          loading="eager"
          fetchPriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#08353D]/85 via-[#0D4B56]/55 to-[#0D4B56]/25" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0D4B56] to-transparent" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-28 pt-36 sm:px-8 lg:px-12">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: DELAY - 0.25, ease }}
          className="mb-6 inline-flex items-center gap-3 rounded-full border border-[#D4AF37]/40 bg-[#08353D]/40 px-5 py-2 text-xs font-bold uppercase tracking-[0.28em] text-[#F3E096] backdrop-blur"
        >
          <span className="h-1.5 w-1.5 rotate-45 bg-[#D4AF37]" />
          Hope Giver · A Community Initiative
        </motion.p>

        <h1 className="font-display text-5xl font-extrabold leading-[1.04] tracking-tight text-white sm:text-6xl lg:text-7xl xl:text-8xl">
          <MaskedLine delay={DELAY}>
            Spreading <span className="font-script text-[6.5rem] font-bold leading-none text-gradient-gold sm:text-[7.5rem] lg:text-[9rem]">hope</span>.
          </MaskedLine>
          <MaskedLine delay={DELAY + 0.15}>
            Creating <span className="font-script text-[6.5rem] font-bold leading-none text-gradient-gold sm:text-[7.5rem] lg:text-[9rem]">change</span>.
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: DELAY + 0.45, ease }}
          className="mt-7 max-w-xl text-lg leading-relaxed text-white/85 sm:text-xl"
        >
          Together, we can make a difference in the lives of those who need it most.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: DELAY + 0.6, ease }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#get-involved"
            data-testid="hero-support-btn"
            onClick={(e) => scrollTo(e, "#get-involved")}
            className="group relative inline-flex items-center gap-2 overflow-hidden rounded-full gold-shimmer px-9 py-4 text-base font-bold text-[#08353D] shadow-[0_14px_40px_rgba(212,175,55,0.4)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(212,175,55,0.55)]"
          >
            Support Our Mission
          </a>
          <a
            href="#get-involved"
            data-testid="hero-get-involved-btn"
            onClick={(e) => scrollTo(e, "#get-involved")}
            className="inline-flex items-center gap-2 rounded-full border-2 border-white/50 px-9 py-4 text-base font-bold text-white backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-[#D4AF37] hover:bg-white/10 hover:text-[#F3E096]"
          >
            Get Involved
          </a>
        </motion.div>
      </motion.div>

      {/* scroll-down indicator */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: DELAY + 0.9, duration: 0.8 }}
        onClick={(e) => scrollTo(e, "#about")}
        aria-label="Scroll down to about section"
        data-testid="hero-scroll-down"
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-[#F3E096]"
      >
        <span className="flex h-14 w-10 items-start justify-center rounded-full border-2 border-[#F3E096]/50 pt-2.5">
          <span className="animate-bounce-soft">
            <ChevronDown size={20} />
          </span>
        </span>
      </motion.button>
    </section>
  );
}
